import { useState } from "react"
import { motion } from "motion/react"
import { router, usePage } from "@inertiajs/react"
import { cn } from "@/lib/utils"

interface ContactLabels {
  full_name: string
  email: string
  message: string
}

interface ContactProps {
  section_title: string
  section_title_highlight: string
  labels: ContactLabels
}

interface PageProps {
  flash: { notice?: string; alert?: string }
  errors?: Record<string, string[]>
  [key: string]: unknown
}

export default function Contact({ section_title, section_title_highlight, labels }: ContactProps) {
  const { props } = usePage<PageProps>()
  const flash = props.flash ?? {}
  const inertiaErrors = props.errors ?? {}

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    content: "",
    nickname: "", // honeypot
  })
  const [submitting, setSubmitting] = useState(false)
  const [localSuccess, setLocalSuccess] = useState(false)

  const errors: Record<string, string> = Object.fromEntries(
    Object.entries(inertiaErrors).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v])
  )

  const hasSuccess = localSuccess || flash.notice?.includes("envoyé")
  const hasError = flash.alert && !hasSuccess

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)

    router.post(
      "/send_message",
      { contact_form: form },
      {
        onSuccess: () => {
          setLocalSuccess(true)
          setForm({ full_name: "", email: "", content: "", nickname: "" })
        },
        onFinish: () => setSubmitting(false),
      }
    )
  }

  return (
    <section id="contact" className="py-24 bg-[#0a0a0f] scroll-mt-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            {section_title}{" "}
            <span className="bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">
              {section_title_highlight}
            </span>
          </h2>
          <div className="w-20 h-px bg-gradient-to-r from-violet-500 to-cyan-500 mx-auto" />
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          onSubmit={handleSubmit}
          className="p-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl space-y-6"
        >
          {/* Honeypot */}
          <input
            type="text"
            name="contact_form[nickname]"
            value={form.nickname}
            onChange={(e) => setForm({ ...form, nickname: e.target.value })}
            tabIndex={-1}
            autoComplete="off"
            style={{ display: "none" }}
            aria-hidden="true"
          />

          {/* Full Name */}
          <div>
            <label htmlFor="fullName" className="block text-sm font-medium text-slate-300 mb-2">
              {labels.full_name}
            </label>
            <input
              id="fullName"
              type="text"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              required
              className={cn(
                "w-full px-4 py-3 bg-white/5 border rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 transition-all",
                errors.full_name ? "border-red-500/50" : "border-white/10 focus:border-violet-500/50"
              )}
              placeholder="John Doe"
            />
            {errors.full_name && (
              <p className="mt-1 text-xs text-red-400">{errors.full_name}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
              {labels.email}
            </label>
            <input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              className={cn(
                "w-full px-4 py-3 bg-white/5 border rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 transition-all",
                errors.email ? "border-red-500/50" : "border-white/10 focus:border-violet-500/50"
              )}
              placeholder="john@example.com"
            />
            {errors.email && (
              <p className="mt-1 text-xs text-red-400">{errors.email}</p>
            )}
          </div>

          {/* Message */}
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-slate-300 mb-2">
              {labels.message}
            </label>
            <textarea
              id="message"
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              required
              rows={5}
              className={cn(
                "w-full px-4 py-3 bg-white/5 border rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 transition-all resize-none",
                errors.content ? "border-red-500/50" : "border-white/10 focus:border-violet-500/50"
              )}
              placeholder="Décrivez votre projet en quelques mots..."
            />
            {errors.content && (
              <p className="mt-1 text-xs text-red-400">{errors.content}</p>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-4 text-sm font-medium text-white bg-violet-600 hover:bg-violet-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-violet-500/20"
          >
            {submitting ? "Envoi en cours..." : "Envoyer le message"}
          </button>

          {/* Inline feedback */}
          {hasSuccess && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center text-sm text-green-400 font-medium"
            >
              ✓ Message envoyé ! Nous vous répondrons dans les 24h.
            </motion.p>
          )}
          {hasError && (
            <p className="text-center text-sm text-red-400">
              Une erreur est survenue. Veuillez réessayer.
            </p>
          )}
        </motion.form>
      </div>
    </section>
  )
}
